
import React, { useContext, useState } from 'react';
import { AppContext } from '../App';
import { supabase, uploadFile } from '../supabase';
import { Role } from '../types';

const Team: React.FC = () => {
  const context = useContext(AppContext);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');

  if (!context) return null;
  const { profiles, setProfiles, roles, setRoles, currentUser, syncAllData } = context;

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsSubmitting(true);
    try {
      const publicUrl = await uploadFile(file);
      setAvatarUrl(publicUrl);
    } catch (err: any) {
      alert('Erro ao fazer upload: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const role = formData.get('role') as any;

    try {
      const { error } = await supabase.from('profiles').update({
        name,
        email,
        role,
        avatar_url: avatarUrl
      }).eq('id', editingProfile.id);

      if (error) throw error;

      setIsUserModalOpen(false);
      setEditingProfile(null);
      setAvatarUrl('');
      await syncAllData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const role = formData.get('role') as any;

    try {
      const { error } = await supabase.from('profiles').insert([{
        id: crypto.randomUUID(),
        name,
        email, // Email should be stored
        role,
        is_first_login: true,
        avatar_url: avatarUrl || `https://ui-avatars.com/api/?name=${name}&background=8a0049&color=fff&rounded=true`
      }]);

      if (error) throw error;

      alert(`Usuário solicitado com sucesso!\n\nInstruções:\n1. Crie o usuário no Supabase Auth com email: ${email}\n2. Defina a senha: 12345678\n3. Vincule o ID gerado ao perfil.`);

      setIsUserModalOpen(false);
      setAvatarUrl('');
      await syncAllData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteProfile = async (id: string) => {
    if (id === currentUser?.id) return alert('Você não pode se auto-excluir.');
    if (confirm('Revogar acesso deste usuário?')) {
      const { error } = await supabase.from('profiles').delete().eq('id', id);
      if (error) alert(error.message);
      await syncAllData();
    }
  };

  return (
    <div className="p-4 md:p-8 pb-32 md:pb-12 max-w-7xl mx-auto flex flex-col gap-6 md:gap-8 animate-in fade-in duration-500 overflow-x-hidden">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-text-main tracking-tight">Gestão de Equipe</h2>
          <p className="text-text-secondary font-bold text-base md:text-lg mt-1">Controle seguro de perfis via Supabase Auth.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
          <button
            onClick={() => setIsUserModalOpen(true)}
            className="h-14 px-8 rounded-2xl bg-primary text-white text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all w-full sm:w-auto flex items-center justify-center gap-3"
          >
            <span className="material-symbols-outlined text-[20px]">person_add</span>
            Novo Membro
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 bg-white rounded-[23px] border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100 text-[10px] font-black text-text-secondary uppercase tracking-widest">
                  <th className="px-10 py-6">Membro</th>
                  <th className="px-10 py-6">Cargo</th>
                  <th className="px-10 py-6">Segurança</th>
                  <th className="px-10 py-6 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {profiles.map(p => (
                  <tr key={p.id} className="hover:bg-primary/[0.01] transition-all group">
                    <td className="px-10 py-6">
                      <div className="flex items-center gap-4">
                        <img src={p.avatarUrl} className="size-12 rounded-2xl border border-gray-100 object-cover" alt="" />
                        <div className="min-w-0">
                          <p className="text-base font-black text-text-main truncate">{p.name}</p>
                          <p className="text-[10px] font-bold text-text-secondary truncate">{p.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-10 py-6">
                      <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-lg tracking-widest ${p.role === 'admin' ? 'bg-primary text-white' : 'bg-gray-100 text-text-secondary'}`}>
                        {p.role}
                      </span>
                    </td>
                    <td className="px-10 py-6">
                      {p.isFirstLogin ? (
                        <span className="text-[9px] font-black uppercase bg-orange-50 text-orange-600 px-2.5 py-1 rounded-lg tracking-widest flex items-center gap-1.5 w-fit">
                          <span className="material-symbols-outlined text-[14px]">lock</span> Senha Temp.
                        </span>
                      ) : (
                        <span className="text-[9px] font-black uppercase bg-green-50 text-green-600 px-2.5 py-1 rounded-lg tracking-widest flex items-center gap-1.5 w-fit">
                          <span className="material-symbols-outlined text-[14px]">verified_user</span> Protegido
                        </span>
                      )}
                    </td>
                    <td className="px-10 py-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setEditingProfile(p);
                            setAvatarUrl(p.avatarUrl);
                            setIsUserModalOpen(true);
                          }}
                          className="size-10 flex items-center justify-center rounded-xl text-gray-300 hover:bg-primary/5 hover:text-primary transition-all"
                        >
                          <span className="material-symbols-outlined text-[20px]">edit</span>
                        </button>
                        <button onClick={() => deleteProfile(p.id)} className="size-10 flex items-center justify-center rounded-xl text-gray-300 hover:bg-red-50 hover:text-red-600 transition-all">
                          <span className="material-symbols-outlined text-[20px]">person_remove</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-4 flex flex-col gap-6">
          <div className="bg-text-main p-10 rounded-[23px] text-white flex flex-col gap-6">
            <div className="size-14 rounded-2xl bg-white/10 flex items-center justify-center text-white">
              <span className="material-symbols-outlined text-3xl">security_update_good</span>
            </div>
            <div className="space-y-2">
              <h4 className="text-xl font-black tracking-tight">Privilégios Administrativos</h4>
              <p className="text-xs font-bold text-white/50 leading-relaxed">
                Como Admin, você pode visualizar todos os perfis, mas por questões de privacidade e segurança, você não pode visualizar ou alterar as senhas pessoais dos membros.
              </p>
            </div>
            <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
              <p className="text-[9px] font-black uppercase tracking-widest text-primary-light">Dica de Segurança</p>
              <p className="text-[10px] font-bold text-white/80 mt-1">Ao criar um novo membro, informe que a senha padrão é <span className="text-white underline">12345678</span>.</p>
            </div>
          </div>
        </div>
      </div>

      {isUserModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-text-main/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-lg rounded-[23px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col">
            <div className="p-8 border-b border-gray-50 flex items-center justify-between shrink-0 bg-white z-10">
              <h3 className="text-2xl font-black text-text-main tracking-tight">
                {editingProfile ? 'Editar Membro' : 'Cadastrar Novo Membro'}
              </h3>
              <button onClick={() => { setIsUserModalOpen(false); setEditingProfile(null); setAvatarUrl(''); }} className="size-12 flex items-center justify-center rounded-full hover:bg-gray-100"><span className="material-symbols-outlined">close</span></button>
            </div>
            <form onSubmit={editingProfile ? handleUpdateUser : handleAddUser} className="p-8 flex flex-col gap-6 overflow-y-auto">
              <div className="flex flex-col items-center gap-4">
                <div className="size-24 rounded-[24px] bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center relative overflow-hidden group shrink-0">
                  {avatarUrl ? (
                    <img src={avatarUrl} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <span className="material-symbols-outlined text-gray-300 text-3xl">add_a_photo</span>
                  )}
                  <label className="absolute inset-0 bg-primary/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <span className="material-symbols-outlined text-white text-xl">upload</span>
                    <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} />
                  </label>
                </div>
                <p className="text-[9px] font-black uppercase text-text-secondary tracking-widest">Foto do Perfil</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">Nome Completo</label>
                <input name="name" defaultValue={editingProfile?.name || ''} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" placeholder="Ex: João da Silva" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">E-mail Corporativo</label>
                <input name="email" type="email" defaultValue={editingProfile?.email || ''} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" placeholder="exemplo@magenta.com" />
              </div>
              <div className="flex flex-col gap-1.5 mb-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">Cargo no Sistema</label>
                <select name="role" defaultValue={editingProfile?.role || 'staff'} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all">
                  <option value="staff">Membro (Staff)</option>
                  <option value="finance">Financeiro</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              <button type="submit" disabled={isSubmitting} className="h-16 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all shrink-0">
                {isSubmitting ? 'Salvando...' : editingProfile ? 'Atualizar Perfil' : 'Gerar Perfil de Acesso'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
